import * as React from 'react'
// libs
import { saveAs } from 'file-saver'
import { useSnackbar } from 'notistack'
// components
import { Menu } from 'components/UI'
// views
import { AddButton } from 'components/Modals/PropertyDetailsModal/components/Form/view'
import { ExampleLink, FileInput } from './views'
// types
import { IFieldOption } from 'types/graphql'
// utils
import pluralize from 'pluralize'
import { readSpreadsheetRows, SPREADSHEET_ACCEPT } from 'utils/csv'
import {
  optionsToCsv,
  parseRowsToOptions,
  mergeImportedOptions
} from 'components/Modals/PropertyDetailsModal/components/Form/components/CustomOptions/utils'

const SAMPLE_FILE_NAME = 'options_import_example.csv'

interface Props {
  isDisabled: boolean
  options: IFieldOption[]
  savedOptions: IFieldOption[]
  fileName?: string
  onImport: (options: IFieldOption[]) => void
}

const ImportExportMenu: React.FC<Props> = ({ isDisabled, options, savedOptions, fileName, onImport }) => {
  const inputFileRef = React.useRef<HTMLInputElement>(null)

  const [isLoading, setIsLoading] = React.useState(false)

  const { enqueueSnackbar } = useSnackbar()

  const onImportClick = () => inputFileRef.current?.click()

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    event.target.value = null

    if (!file) {
      return
    }

    setIsLoading(true)

    try {
      const rows = await readSpreadsheetRows(file, { raw: true })

      const { items, skipped } = parseRowsToOptions(rows)

      if (!items.length) {
        enqueueSnackbar('No valid rows found in the file', { variant: 'warning' })
        return
      }

      onImport(mergeImportedOptions(options, items))

      const skippedMessage = skipped ? `, skipped ${skipped} invalid or duplicated ${pluralize('row', skipped)}` : ''

      enqueueSnackbar(`Imported ${items.length} ${pluralize('option', items.length)}${skippedMessage}`, {
        variant: skipped ? 'warning' : 'success'
      })
    } catch (error) {
      console.error('[ImportExportMenu.onFileChange]:', error)

      enqueueSnackbar('Unable to read the file', { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const onExportClick = () => {
    const blob = new Blob([optionsToCsv(savedOptions)], { type: 'text/csv;charset=utf-8' })

    saveAs(blob, `${fileName || 'options'}.csv`)
  }

  return (
    <>
      <Menu
        disableAutoFocusItem
        trigger={
          <AddButton data-testid='picklist-import-export' $disabled={isLoading}>
            import / export
          </AddButton>
        }>
        {!isDisabled && (
          <span data-testid='picklist-import-options' onClick={onImportClick}>
            Import Options
          </span>
        )}
        {!isDisabled && (
          <ExampleLink href={`/files/${SAMPLE_FILE_NAME}`} download={SAMPLE_FILE_NAME}>
            Download Sample
          </ExampleLink>
        )}
        {!!savedOptions.length && (
          <span data-testid='picklist-export-options' onClick={onExportClick}>
            Export Options
          </span>
        )}
      </Menu>

      <FileInput
        type='file'
        ref={inputFileRef}
        onChange={onFileChange}
        data-testid='picklist-import-input'
        accept={SPREADSHEET_ACCEPT}
      />
    </>
  )
}

export default ImportExportMenu
